import Axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import jwtDecode from "jwt-decode";

import { IToken } from 'interfaces/token';
import { SECOND } from 'utils/date';
import { getToken, removeTokenWithRedirect } from 'utils/token';
import { refreshToken } from 'services/refreshToken';
import { API_BASE_URI } from 'const';

import errorHandler from './errorHandler';

const axiosInstance = Axios.create({
	baseURL: `${API_BASE_URI}/api`,
	headers: { 'Content-Type': 'application/json' },
});

export const setAuthorizationToken = (token: IToken): void => {
	axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token.access_token}`;
};

export const removeAuthorizationToken = (): void => {
	delete axiosInstance.defaults.headers.common['Authorization'];
};

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

export const initTokenRefresh = async (forceUpdate: boolean = true, config?: AxiosRequestConfig): Promise<void> => {
	let token: IToken | null = getToken();
	if (forceUpdate) {
		token = await refreshToken(config);
	}
	if (!token) return;
	const decoded: { exp: number } = jwtDecode(token?.access_token || '');
	const currentDate = Date.now();
	const expiredIn = (decoded?.exp * SECOND) - currentDate;
	const refreshTime = expiredIn - (30 * SECOND);
	if (refreshTimer) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
	refreshTimer = setTimeout(() => {
		initTokenRefresh();
	}, refreshTime);
};

axiosInstance.interceptors.response.use(
	(config: AxiosResponse): AxiosResponse => config,
	(error: any): Promise<never> => {
		errorHandler(
			(config: AxiosResponse): Promise<void> => initTokenRefresh(true, config),
			() => removeTokenWithRedirect(),
		)(error);
		return Promise.reject(error);
	},
);

export type Response<T> = AxiosPromise<T>;

export default axiosInstance;
